/**
 * The one score ladder the console ranks search hits by.
 *
 * It lives in the data layer, not in the palette, because two sources feed one
 * ranked list: `searchConsole` scores accounts, the live book and transfers,
 * while the palette scores actions and pages client-side so they land on the
 * first keystroke regardless of network latency. Two copies of this table would
 * drift within a week and the interleaving would stop meaning anything.
 *
 * Everything here is pure and has no world access, so `search.test.ts` can pin
 * the ordering rules the owner named without building a fixture world.
 */

import type { SearchKind } from './types'

export type MatchQuality = 'exact' | 'prefix' | 'word-prefix' | 'substring' | 'subsequence'

/**
 * How a field's weight is applied. The weight multiplies the MATCH component
 * only, never the base: a substring hit in a remark should not be able to drag a
 * transfer below its own floor, and an exact hit on a username should not be
 * worth less because the kind it belongs to is cheap.
 */
export type FieldWeight = 1 | 0.8 | 0.6

export const SEARCH_SCORE = {
  /**
   * Base by kind. An operator searching by name is searching for a person, so
   * accounts outrank everything; `action` sits above `transfer` so typing "give"
   * finds the action, while a username like "rk.master01" still beats it on
   * match quality alone.
   */
  base: { account: 1000, market: 900, action: 850, transfer: 700 } as Record<SearchKind, number>,

  quality: {
    exact: 500,
    prefix: 300,
    'word-prefix': 200,
    substring: 80,
    subsequence: 20,
  } as Record<MatchQuality, number>,

  /** username / transfer ref / action verb · display + event name · everything else. */
  weight: { primary: 1, secondary: 0.8, tertiary: 0.6 } as Record<
    'primary' | 'secondary' | 'tertiary',
    FieldWeight
  >,

  /** Added flat, after the weighted match — liveness is a property of the row. */
  live: {
    /** An in-play market is the risk you cannot walk away from. */
    inPlay: 120,
    activeToday: 40,
    recentTransfer: 30,
  },

  /** Below this, a subsequence match is noise: "abc" matches almost anything. */
  minSubsequence: 3,
} as const

/**
 * Usernames in this domain are `rk.master01`, `demo@fancy`, `66delhi@15`. A
 * plain whitespace split would make word-prefix matching useless on exactly the
 * field it matters most for.
 */
export const SEARCH_TOKEN_SPLIT = /[\s._@\-/]+/

/** Token start offsets in the original string, including 0. */
function tokenStarts(field: string): number[] {
  const starts = [0]
  const re = new RegExp(SEARCH_TOKEN_SPLIT.source, 'g')
  let match: RegExpExecArray | null
  while ((match = re.exec(field)) !== null) {
    const next = match.index + match[0].length
    if (next < field.length) starts.push(next)
    if (re.lastIndex === match.index) re.lastIndex++
  }
  return starts
}

export interface FieldMatch {
  quality: MatchQuality
  /** [start,end) offsets into the ORIGINAL-case field. */
  ranges: [number, number][]
}

/**
 * How well `needle` matches `field`, and where.
 *
 * Comparison is on the lowercased pair, but every offset is into the original
 * string so highlighting survives mixed case ("Manchester" highlighted from a
 * "manch" query must mark M-a-n-c-h, not m-a-n-c-h shifted by nothing).
 */
export function matchField(field: string, needle: string): FieldMatch | null {
  const hay = field.toLowerCase()
  const pin = needle.trim().toLowerCase()
  if (!pin) return null

  if (hay === pin) return { quality: 'exact', ranges: [[0, field.length]] }

  const occurrences: [number, number][] = []
  for (let at = hay.indexOf(pin); at !== -1; at = hay.indexOf(pin, at + pin.length)) {
    occurrences.push([at, at + pin.length])
  }

  if (occurrences.length > 0) {
    const starts = new Set(tokenStarts(field))
    const quality: MatchQuality = occurrences[0]![0] === 0
      ? 'prefix'
      : occurrences.some(([start]) => starts.has(start))
        ? 'word-prefix'
        : 'substring'
    return { quality, ranges: occurrences }
  }

  if (pin.length < SEARCH_SCORE.minSubsequence) return null

  // Letters in order, not necessarily adjacent. Deliberately last and cheap:
  // it exists so a half-remembered username still surfaces, not so every query
  // returns something.
  const ranges: [number, number][] = []
  let cursor = 0
  for (const ch of pin) {
    const at = hay.indexOf(ch, cursor)
    if (at === -1) return null
    const last = ranges[ranges.length - 1]
    if (last && last[1] === at) last[1] = at + 1
    else ranges.push([at, at + 1])
    cursor = at + 1
  }
  return { quality: 'subsequence', ranges }
}

/** Base + weighted match quality. Liveness bonuses are added by the caller. */
export function scoreMatch(kind: SearchKind, match: FieldMatch, weight: FieldWeight): number {
  return SEARCH_SCORE.base[kind] + SEARCH_SCORE.quality[match.quality] * weight
}

/**
 * Rank within a group: score first, then the SHORTER title, then alphabetical.
 *
 * Shorter-first is the tie-break that makes "raj" put `raj` above `rajesh_07`
 * when both matched as a prefix — the operator typed the whole of one of them.
 */
export function compareHits(
  a: { score: number; title: string },
  b: { score: number; title: string },
): number {
  if (b.score !== a.score) return b.score - a.score
  if (a.title.length !== b.title.length) return a.title.length - b.title.length
  return a.title.localeCompare(b.title, 'en-IN')
}
